import * as React from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import moment from 'moment'
import I18n from '../../../../core/i18n'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button } from '../../../atoms'

import { ISelectedGuests } from 'utils/reservation'
import { ReservationSelectedDatesDate } from './atoms/ReservationSelectedDatesDate'
import SvgIcon from 'components/atoms/SvgIcon'
import { clearReservationStorageAndInfo } from 'utils/reservationSessionStorage'

interface IProps {
  post: interfaces.IPost
  memberPost: interfaces.IPost
  guests: ISelectedGuests
  reservation: {
    selectedDate: moment.Moment
    totalPrice: number
    departure: any
  }
  createdReservation: any
}

export const ReservationResult = ({
  guests,
  post,
  memberPost,
  reservation,
  createdReservation,
}: IProps) => {
  const isInstant = memberPost?.instant_booking

  const postDetail = React.useMemo(() => {
    if (memberPost?.post_detail) {
      return memberPost?.post_detail
    }
    return memberPost?.post_details?.length > 0 ? memberPost?.post_details[0] : null
  }, [memberPost])

  const adultAndChildCount = guests.adults + (guests.children ?? 0)
  const infantCount = guests.infants ?? 0
  const shouldShowPickup =
    createdReservation?.post?.post_type === 'private' && createdReservation?.post?.allow_pickup

  const includedCategoryItems = [
    {
      label: I18n.t('post.included_category_food'),
      value: postDetail?.food,
    },
    {
      label: I18n.t('post.included_category_drinks'),
      value: postDetail?.drinks,
    },
    {
      label: I18n.t('post.included_category_tickets'),
      value: postDetail?.tickets,
    },
    {
      label: I18n.t('post.included_category_transportation'),
      value: postDetail?.transportation,
    },
    {
      label: I18n.t('post.included_category_equipment'),
      value: postDetail?.equipment,
    },
  ].filter(item => String(item.value || '').trim().length > 0)

  const hasIncludedCategories = includedCategoryItems.length > 0

  const includedText = String(postDetail?.included || '').trim()
  const notIncludedText = String(postDetail?.not_included || '').trim()
  const shouldShowIncluded = hasIncludedCategories || includedText.length > 0
  const shouldShowNotIncluded = notIncludedText.length > 0

  return (
    <>
      <GlobalStyle />
      <ReservationResultWrapper>
        {createdReservation ? (
          <>
            <h2 className="ReservationNew_CompletedHeader">
              <SvgIcon name="check" className="icon" ariaHidden={true} />
              {isInstant ? (
                <span>
                  {I18n.t('reservation.reservation_thank')}
                  <br />
                  {I18n.t('reservation.reservation_completed')}
                </span>
              ) : (
                <span>Reservation received</span>
              )}
            </h2>

            <h4>{I18n.t('reservation.reservation_booking_details')}</h4>
            {isInstant ? (
              <div>
                <p>Your booking has been successfully completed.</p>
                <br />
                <p>
                  A confirmation email has been sent immediately to the email address you provided:
                </p>
                <p className="email">{createdReservation.guest_email}</p>
              </div>
            ) : (
              <>
                <p className="preWrap">
                  {I18n.t('reservation.reservation_booking_complete_condition')}
                </p>
                <p>
                  {I18n.t('reservation.reservation_email_notice')}
                  <br />
                  <p className="email">{createdReservation.guest_email}</p>
                </p>
              </>
            )}
            <div>
              <p>If you do not receive the email, please check your spam or junk mail folder.</p>
              <p>It is also possible that the email address was entered incorrectly.</p>
            </div>
            <div>
              <p>If you still cannot find the email, please contact us using the form below:</p>
              <a href={constants.INQUIRY_LINK} target="_blank" rel="noopener noreferrer">
                Inquiry Form
              </a>
            </div>
            {isInstant && (
              <div className="boxItem">
                <h5>Made a mistake with your booking?</h5>
                <p>
                  Cancel for free within 1 hour of purchase. Please check your confirmation email
                  and follow the link to cancel. After this time limit, standard cancellation fees
                  and a 5% system fee will apply.
                </p>
              </div>
            )}
            <div className="boxItem">
              <h5>Booking Number</h5>
              <p>{createdReservation.no}</p>
            </div>
            <div className="boxItem">
              <h5>{I18n.t('reservation.reservation_date')}</h5>
              <ReservationSelectedDatesDate
                date={{ date: reservation.selectedDate }}
                startTime={reservation.departure}
                post={post}
                fontSize={20}
              />
            </div>

            <div className="boxItem">
              <h5>{I18n.t('reservation.reservation_tour_name')}</h5>
              <h6>{createdReservation?.post?.name}</h6>
              {post.family_owner && <p>{post.name}</p>}
            </div>

            <div className="boxItem">
              <h5>{I18n.t('reservation.reservation_number_participants')}</h5>
              <p>
                {adultAndChildCount}
                {infantCount > 0 && ` (Infants: +${infantCount})`}
              </p>
            </div>

            <div className="boxItem">
              <h5>{I18n.t('reservation.reservation_meeting_point')}</h5>
              {memberPost.meeting_points[0] && (
                <>
                  <p>{memberPost.meeting_points[0].name ?? ''}</p>
                  <div className="meetingPointInfo">
                    <p>{memberPost.meeting_points[0].location ?? ''}</p>
                    {memberPost.meeting_points[0].memo && (
                      <p className="preWrap">{memberPost.meeting_points[0].memo}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {shouldShowPickup && (
              <div className="boxItem">
                <h5>{I18n.t('reservation.reservation_pickup_location')}</h5>
                <p>{createdReservation.pickup_location || ''}</p>
              </div>
            )}

            {shouldShowIncluded && (
              <div className="boxItem">
                <h5>{I18n.t('reservation.reservation_included')}</h5>
                {hasIncludedCategories ? (
                  <>
                    {includedCategoryItems.map(item => (
                      <React.Fragment key={item.label}>
                        <h6>{item.label}</h6>
                        <p className="preWrap">{item.value}</p>
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  <p className="preWrap">{includedText}</p>
                )}
              </div>
            )}

            {shouldShowNotIncluded && (
              <div className="boxItem">
                <h5>{I18n.t('reservation.reservation_not_included')}</h5>
                <p className="preWrap">{notIncludedText}</p>
              </div>
            )}

            <div className="boxItem">
              <h5>{I18n.t('reservation.reservation_other_requests')}</h5>
              <p className="preWrap">
                {createdReservation.other_requests === ''
                  ? 'Nothing'
                  : createdReservation.other_requests}
              </p>
            </div>
            <div className="ReservationNew_CompletedButtons">
              <Button
                largeAlt={true}
                handleClick={() => {
                  clearReservationStorageAndInfo()
                  setTimeout(() => {
                    window.location.href = `/posts/${post.slug}`
                  }, 300)
                }}
              >
                {I18n.t('reservation.back_to_posts')}
              </Button>
            </div>
          </>
        ) : null}
      </ReservationResultWrapper>
    </>
  )
}

const GlobalStyle = createGlobalStyle`
  .header-wrapper, .Scroller, .mainFooter {
    display: none !important;
  }
`

const ReservationResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  .ReservationNew_CompletedHeader {
    font-size: 3.2rem;
    font-weight: 700;
    line-height: 4.4rem;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .icon {
      width: 4rem;
      height: 4rem;
      padding: 0.8rem;
      margin-right: 2rem;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.primary};
      border-radius: 50%;
    }
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 2.4rem;
      font-weight: 700;
      line-height: 3.2rem;
      .icon {
        width: 3.2rem;
        height: 3.2rem;
        padding: 0.4rem;
        margin-right: 1.6rem;
      }
    }
  }

  .ReservationNew_CompletedMessage {
    white-space: pre-wrap;
  }

  .ReservationNew_CompletedButtons {
    margin: 24px 0;
    display: flex;
    justify-content: flex-start;
    width: 100%;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      width: 100%;
      padding: 0;
    }
    button {
      width: 100%;
    }
  }
  .preWrap {
    white-space: pre-wrap;
  }

  p {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${constants.COLORS.black};
    &.email {
      color: ${constants.COLORS.primary};
      margin-top: 0.4rem;
      word-break: break-all;
    }
  }
  a {
    color: ${constants.COLORS.primary};
    font-size: 16px;
    line-height: 24px;
  }

  h4 {
    color: ${constants.COLORS.black};
    font-size: 2.4rem;
    font-weight: 700;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 2rem;
    }
  }

  .boxItem {
    display: flex;
    flex-direction: column;
    gap: 12px;
    h5 {
      color: ${constants.COLORS.subText};
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
    }
    h6 {
      color: ${constants.COLORS.black};
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
    }
    p {
      word-break: keep-all;
      overflow-wrap: anywhere;
    }
  }

  .meetingPointInfo {
    margin-top: 8px;
    p {
      font-size: 14px;
    }
  }

  .infoBox {
    color: ${constants.COLORS.subText};
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    strong {
      color: ${constants.COLORS.black};
      font-weight: 700;
    }
  }
`
